import { useState, useEffect, useRef, useMemo, Children } from 'react';
import {
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Chip,
  ListItemButton,
  Button,
  Card as MuiCard,
  CardContent,
  Stack,
  Avatar as MuiAvatar,
  SvgIcon,
  LinearProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faClock,
  faPlane,
  faBriefcase,
  faChartLine,
  faWallet,
  faFileAlt,
  faChartBar,
  faCog,
  faBell,
  faSearch,
  faChevronRight,
  faRobot,
  faPaperPlane,
  faTimes,
  faPlus,
  faCheck,
  faBuilding,
  faAward,
  faSignOutAlt,
  faBook,
  faShieldAlt,
  faSparkles,
  faArrowRight,
  faArrowLeft,
  faDownload,
  faUser,
  faStar,
  faMapPin,
  faEnvelope,
  faPhone,
  faPencil,
  faAlertTriangle,
  faHashtag,
  faCalendar,
  faGraduationCap,
  faUserPlus,
  faComment,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  Wallet,
  Users,
  ShieldCheck,
  Plus,
  Check,
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building2,
  GraduationCap,
  Clock,
  X,
  CalendarOff,
  CircleUser,
  Sparkles,
  TrendingUp,
  TrendingDown,
  LayoutDashboard,
  FileText,
  BarChart3,
  Home,
  BookOpen,
  UserPlus,
  AlertTriangle,
  Settings,
  Search,
  Bell,
  Send,
  Hash,
  Calendar,
  Pencil,
  CornerDownLeft,
  ChevronRight,
  MessageSquare,
  LogOut,
  Bot,
  Download,
  Star,
  PanelLeft,
  UserCog,
  SlidersHorizontal,
  Award,
  Menu as MenuIcon,
} from 'lucide-react';
import { api } from './api';
import EmployeesDataGrid from './EmployeesDataGrid';
import AppDataGrid, {
  DataGridCard,
  DataGridPage,
  PersonCell,
  TextCell,
  GridStatusPill,
  useAttendanceColumns,
  useHistoryColumns,
} from './AppDataGrid';
import { useAuth } from './auth';
import { useAppSettings } from './SettingsContext';
import { buildPath, parsePath } from './routing';
import {
  StatusPill,
  UserAvatar,
  Header,
  SectionHeader,
  GridLayout,
  EmptyState,
} from './components';
import { FONT, AG, PIE_COLORS, STATUS_COLOR, statusTone } from './theme/tokens';
import { initials, empEmail, tenure, fmtPKR, fmtM, greeting } from './lib/format';
import { STAGES, JOB_STAGES, jobTone, jobName } from './lib/ats';
import { printPayDoc, docRow, printReport } from './lib/print';
import { OKR_RATINGS, RATING_NUM, ratingTone, okrStatusTone, okrTabBtn, okrLbl, weightedProgress, avgRatingLabel, okrsByEmployee } from './lib/okr';
import { Pill, Avatar, AccountButton, Card, DashboardWidget, WidgetGrid, KPI, CardSectionHeader, ChartCard, SectionTitle, ChartTip, CenterSpinner, PrimaryBtn, OutlineBtn, PageScroll, PTh, PTd, PerfTable, DrawerFormRow, DrawerSection, DrawerTextField, DRAWER_TABLE_SX } from './ui';

function CommandPalette({
  open,
  onClose,
  q = '',
  isHR,
  employees,
  candidates,
  navTo,
  openEmployee,
  openCandidate,
  doAction,
  askAI,
}) {
  const [sel, setSel] = useState(0);
  const resultsRef = useRef([]);

  useEffect(() => { if (open) setSel(0); }, [open]);
  useEffect(() => { setSel(0); }, [q]);

  const navAll = isHR
    ? [
        {
          cat: 'Navigation',
          icon: LayoutDashboard,
          label: 'Dashboard',
          run: () => navTo('Dashboard'),
        },
        {
          cat: 'Navigation',
          icon: Users,
          label: 'Employees',
          run: () => navTo('Employees'),
        },
        {
          cat: 'Navigation',
          icon: Clock,
          label: 'Attendance',
          run: () => navTo('Attendance'),
        },
        {
          cat: 'Navigation',
          icon: CalendarOff,
          label: 'Leave management',
          run: () => navTo('Leave'),
        },
        {
          cat: 'Navigation',
          icon: Briefcase,
          label: 'Recruitment',
          run: () => navTo('Recruitment'),
        },
        {
          cat: 'Navigation',
          icon: TrendingUp,
          label: 'Performance',
          run: () => navTo('Performance'),
        },
        {
          cat: 'Navigation',
          icon: Wallet,
          label: 'Payroll',
          run: () => navTo('Payroll'),
        },
        {
          cat: 'Navigation',
          icon: BarChart3,
          label: 'Reports',
          run: () => navTo('Reports'),
        },
      ]
    : [
        {
          cat: 'Navigation',
          icon: Home,
          label: 'Dashboard',
          run: () => navTo('Dashboard'),
        },
        {
          cat: 'Navigation',
          icon: Clock,
          label: 'Attendance',
          run: () => navTo('Attendance'),
        },
        {
          cat: 'Navigation',
          icon: CalendarOff,
          label: 'Leave',
          run: () => navTo('Leave'),
        },
        {
          cat: 'Navigation',
          icon: Wallet,
          label: 'Payroll',
          run: () => navTo('Payroll'),
        },
        {
          cat: 'Navigation',
          icon: FileText,
          label: 'Documents',
          run: () => navTo('Documents'),
        },
        {
          cat: 'Navigation',
          icon: BookOpen,
          label: 'Policies',
          run: () => navTo('Policies'),
        },
      ];
  const actionAll = isHR
    ? [
        {
          cat: 'Actions',
          icon: Wallet,
          label: 'Run payroll',
          run: () => doAction('run-payroll'),
        },
        {
          cat: 'Actions',
          icon: UserPlus,
          label: 'Add employee',
          run: () => doAction('add-employee'),
        },
        {
          cat: 'Actions',
          icon: Briefcase,
          label: 'New job requisition',
          run: () => doAction('new-job'),
        },
      ]
    : [
        {
          cat: 'Actions',
          icon: CalendarOff,
          label: 'Apply for leave',
          run: () => doAction('apply-leave'),
        },
        {
          cat: 'Actions',
          icon: Clock,
          label: 'Clock in / out',
          run: () => doAction('clock'),
        },
      ];
  // Build grouped result sets — employees & candidates only surface when typing.
  const empMatches = q.length > 0
    ? (employees || []).filter(e => (e.name + ' ' + (e.role || '') + ' ' + (e.dept || '')).toLowerCase().includes(q.toLowerCase())).slice(0, 5)
    : [];
  const candMatches = isHR && q.length > 0
    ? (candidates || []).filter(c => (c.name + ' ' + (c.role || '')).toLowerCase().includes(q.toLowerCase())).slice(0, 3)
    : [];
  const navMatches = navAll.filter(n => !q || n.label.toLowerCase().includes(q.toLowerCase()));
  const actionMatches = actionAll.filter(a => !q || a.label.toLowerCase().includes(q.toLowerCase()));
  const showAI = q.length > 1;

  // Flat list (in display order) for keyboard navigation.
  const flatItems = [
    ...empMatches.map(e => ({ run: () => { openEmployee(e.id); onClose(); } })),
    ...candMatches.map(c => ({ run: () => { openCandidate(c.id); onClose(); } })),
    ...navMatches.map(n => ({ run: () => { n.run(); onClose(); } })),
    ...actionMatches.map(a => ({ run: () => { a.run(); onClose(); } })),
    ...(showAI ? [{ run: () => { askAI(q); onClose(); } }] : []),
  ];
  resultsRef.current = flatItems;

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      const r = resultsRef.current;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSel((s) => Math.min(r.length - 1, s + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSel((s) => Math.max(0, s - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        r[sel]?.run();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, sel]);

  if (!open) return null;

  const KbdKey = ({ children }) => (
    <Box component="span" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '4px', px: '5px', py: '1px', fontSize: 10, color: 'text.secondary', fontFamily: 'monospace' }}>{children}</Box>
  );

  const SectionLabel = ({ label, divider }) => (
    <Typography
      variant="caption"
      sx={{
        display: 'block', px: 2, pt: divider ? 1.25 : 1, pb: 0.5, mt: divider ? 0.5 : 0,
        fontWeight: 700, fontSize: 10.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.disabled',
        borderTop: divider ? '1px solid' : 'none', borderColor: 'divider',
      }}
    >
      {label}
    </Typography>
  );

  // Section offsets so each row's index lines up with flatItems (for keyboard nav).
  const candBase = empMatches.length;
  const navBase = candBase + candMatches.length;
  const actBase = navBase + navMatches.length;
  const aiIndex = actBase + actionMatches.length;

  const Row = ({ index, onRun, children }) => {
    const selected = sel === index;
    return (
      <Box
        component="button"
        onClick={onRun}
        onMouseEnter={() => setSel(index)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', px: 2, py: 1,
          border: 'none', cursor: 'pointer', textAlign: 'left',
          bgcolor: selected ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background 0.1s',
        }}
      >
        {children}
        {selected && <CornerDownLeft size={13} style={{ color: AG.primary, flexShrink: 0, marginLeft: 'auto' }} />}
      </Box>
    );
  };

  const hasResults = empMatches.length || candMatches.length || navMatches.length || actionMatches.length;

  return (
    <Box
      className="scale-in"
      sx={{
        position: 'absolute',
        top: 'calc(100% + 6px)',
        left: 0,
        width: '100%',
        minWidth: 360,
        bgcolor: 'background.paper',
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 4px 6px -2px rgba(16,24,40,0.04), 0 12px 28px -6px rgba(16,24,40,0.18)',
        zIndex: 1200,
      }}
      >
        {/* Results (the input lives in the header search bar) */}
        <Box sx={{ overflowY: 'auto', maxHeight: 'min(440px, calc(100vh - 130px))', py: 0.5 }}>
          {!hasResults && (
            <Typography sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 13.5, py: 5 }}>
              {q ? `No results for "${q}"` : 'Start typing to search…'}
            </Typography>
          )}

          {/* Employees */}
          {empMatches.length > 0 && (
            <>
              <SectionLabel label="Employees" />
              {empMatches.map((e, i) => (
                <Row key={e.id} index={i} onRun={() => { openEmployee(e.id); onClose(); }}>
                  <Avatar name={e.name} size="sm" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.3 }} noWrap>{e.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }} noWrap>{e.role}{e.dept ? ` · ${e.dept}` : ''}</Typography>
                  </Box>
                </Row>
              ))}
            </>
          )}

          {/* Candidates */}
          {candMatches.length > 0 && (
            <>
              <SectionLabel label="Candidates" divider={candBase > 0} />
              {candMatches.map((c, i) => (
                <Row key={c.id} index={candBase + i} onRun={() => { openCandidate(c.id); onClose(); }}>
                  <Avatar name={c.name} size="sm" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.3 }} noWrap>{c.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }} noWrap>{c.role} · {c.stage}</Typography>
                  </Box>
                </Row>
              ))}
            </>
          )}

          {/* Navigation */}
          {navMatches.length > 0 && (
            <>
              <SectionLabel label="Navigation" divider={navBase > 0} />
              {navMatches.map((n, i) => (
                <Row key={n.label} index={navBase + i} onRun={() => { n.run(); onClose(); }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <n.icon size={15} color={AG.n500} />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{n.label}</Typography>
                </Row>
              ))}
            </>
          )}

          {/* Actions */}
          {actionMatches.length > 0 && (
            <>
              <SectionLabel label="Actions" divider={actBase > 0} />
              {actionMatches.map((a, i) => (
                <Row key={a.label} index={actBase + i} onRun={() => { a.run(); onClose(); }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: AG.primaryLightest || '#FCE8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <a.icon size={15} color={AG.primary} />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{a.label}</Typography>
                </Row>
              ))}
            </>
          )}

          {/* AI row when typing */}
          {showAI && (
            <Box sx={{ borderTop: hasResults ? '1px solid' : 'none', borderColor: 'divider', mt: hasResults ? 0.5 : 0, pt: hasResults ? 0.5 : 0 }}>
              <Row index={aiIndex} onRun={() => { askAI(q); onClose(); }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: `linear-gradient(135deg,${AG.purple},${AG.primary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Sparkles size={15} color="#fff" />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>Ask HR assistant: <em>"{q}"</em></Typography>
              </Row>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'text.disabled' }}>
            <KbdKey>↑↓</KbdKey> <span>navigate</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'text.disabled' }}>
            <KbdKey>↵</KbdKey> <span>select</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'text.disabled', ml: 'auto' }}>
            <Sparkles size={11} style={{ color: AG.purple }} /> <span>AI-powered search</span>
          </Box>
        </Box>
      </Box>
  );
}

// FFApp drawer form row — label left, filled input right (editable-tasks-drawer table).

export { CommandPalette };
